import React, { useState, useRef, useLayoutEffect, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SignatureCanvas from 'react-signature-canvas'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { toast } from '../ui/Toast'
import TextInput from '../inputs/TextInput'
import BackButton from '../buttons/BackButton'
import SubmitButton from '../buttons/SubmitButton'
import FormTemplate from '../FormTemplate'
import { useStepTransition, STEP_LEAVE_MS } from '../../hooks/useStepTransition'
import { Heading, HeadingMuted, FinePrint, Lead } from '../ui/Text'
import { JOURNEY_PROGRESS } from '../ui/JourneyProgress'
import { InputFieldDate } from '../reusable/inputFieldDate'
import { InputFieldText } from '../reusable/inputFieldText'
import { InputFieldAddressLookup } from '../reusable/inputFieldAddressLookup'
import { InputFieldGoogleAddressLookup } from '../reusable/inputFieldGoogleAddressLookup'
import { InputCheckbox } from '../reusable/inputCheckbox'
import { DocumentPreviewCard } from '../reusable/documentPreviewCard'
import { SignatureActionBar } from '../reusable/signatureActionBar'
import { GooglePlaceAddress } from '../../services'
import { dialCodes } from '../../data/dialCodes'
import { trackMetaLead } from '../../helpers/handleTracking'
import {
    LEAD_SIGNATURE_CONSENT_DISPLAY,
    LEAD_SIGNATURE_CONSENT_SUPPORTING,
} from '../../helpers/mhlLeadSignature'
import { FormData, SERVICE_BRANCHES, ServiceRecord, getLeadPreviewFingerprint } from '../../hooks/useFormData'
import styles from './styles.module.scss'
import sigPadStyles from '../reusable/sigPad/styles.module.scss'

type PreviewDocument = {
    id: string
    title: string
    url?: string | null
    description?: string
}

const PREVIEW_DOC_DESCRIPTIONS: Record<string, string> = {
    'bridging-agreement': 'Agreement bridging your matter with Sentinel Legal. Please review carefully before signing.',
    'conditional-fee-agreement': 'Your Conditional Fee Agreement (CFA) with Sentinel Legal.',
    'pre-action-contingency-fee': 'Pre-action contingency fee agreement for your claim.',
    'foa-service-records': 'Authority to obtain your military service records.',
    'mod-sar-request': 'MOD subject access request for your records.',
    'civilian-medical-consent': 'Consent for civilian medical records (where applicable).',
    'hmrc-employment-history': 'HMRC employment history request form.',
}

gsap.registerPlugin(ScrollToPlugin)

type Step = 1 | 2 | 3 | 4 | 5 | 6

// How long the glide back up to the top of the form takes before a step change
const SCROLL_UP_MS = 500
// Space left above the stepper ("Eligibility / Your details") when gliding up to it
const SCROLL_UP_GAP = 20

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// A service card folded away: its height, padding and the 16px list gap after it
const SERVICE_FOLDED = { height: 0, paddingTop: 0, paddingBottom: 0, marginBottom: -16, opacity: 0 }

// Opens to its content's height, then brings its children in one after another.
// `appear` false skips it, e.g. when it's already there as the step arrives.
const Reveal: React.FC<{ appear: boolean; className?: string; children: React.ReactNode }> = ({
    appear,
    className,
    children,
}) => {
    const ref = useRef<HTMLDivElement>(null)
    useGSAP(
        () => {
            const el = ref.current
            if (!appear || !el || reducedMotion()) return
            const tl = gsap.timeline()
            tl.fromTo(
                el,
                { height: 0, overflow: 'hidden' },
                { height: 'auto', duration: 0.45, ease: 'power3.out', clearProps: 'height,overflow' },
            )
            tl.fromTo(
                el.children,
                { opacity: 0, y: 8 },
                { opacity: 1, y: 0, duration: 0.35, stagger: 0.06, ease: 'power2.out', clearProps: 'opacity,transform' },
                0.05,
            )
        },
        { scope: ref },
    )
    return (
        <div ref={ref} className={className}>
            {children}
        </div>
    )
}
type AddressRegion = 'uk' | 'international'

const countryOptions = [...dialCodes].sort((a, b) => a.name.localeCompare(b.name))

interface ClaimFormProps {
    formData: FormData
    onFieldChange: (field: keyof FormData, value: string) => void
    setServiceField: <K extends keyof ServiceRecord>(index: number, field: K, value: ServiceRecord[K]) => void
    setSigData: (sigData: string) => void
    setSignatureConsentChecked: (checked: boolean) => void
    addService: () => void
    removeService: (index: number) => void
    saveDateOfBirth: (obj: { field: string; value: string }) => { success: boolean; error: string }
    showDateErrors: boolean
    triggerDateErrors: () => void
    saveEmail: (obj: { field: string; value: string }) => { success: boolean; error: string }
    showEmailErrors: boolean
    triggerEmailErrors: () => void
    savePhone: (obj: { field: string; value: string }) => { success: boolean; error: string }
    showPhoneErrors: boolean
    triggerPhoneErrors: () => void
    saveAddress: (obj: { field: string; value: string }) => { success: boolean; error: string }
    showServiceDateErrors: boolean
    triggerServiceDateErrors: () => void
    showServiceNumberErrors: boolean
    triggerServiceNumberErrors: () => void
    submitFormData: (url?: string) => Promise<{
        success: boolean
        data: any
        error: string | null
        code?: string
    }>
    previewFormDocuments: (url?: string) => Promise<{
        success: boolean
        data: any
        error: string | null
        code?: string
    }>
    onComplete?: (data: FormData) => void
    onBack?: () => void
    onReset?: () => void
    resetFormData?: () => void
}

// The only titles chosen in production, so a tap is quicker than opening a list
const TITLE_OPTIONS = ['Mr', 'Mrs', 'Miss', 'Ms']


const STEP_LABELS = [
    'Basic details',
    'Contact details',
    'And where do you currently live?',
    'Tell us about your time in the forces',
    'And your National Insurance number',
    'Sign to instruct us',
]

const ClaimForm: React.FC<ClaimFormProps> = ({
    formData,
    onFieldChange,
    setServiceField,
    setSigData,
    setSignatureConsentChecked,
    addService,
    removeService,
    saveDateOfBirth,
    showDateErrors,
    triggerDateErrors,
    saveEmail,
    showEmailErrors,
    triggerEmailErrors,
    savePhone,
    showPhoneErrors,
    triggerPhoneErrors,
    saveAddress,
    showServiceDateErrors,
    triggerServiceDateErrors,
    showServiceNumberErrors,
    triggerServiceNumberErrors,
    submitFormData,
    previewFormDocuments,
    onComplete,
    onBack,
}) => {
    const navigate = useNavigate()
    const [currentStep, setCurrentStep] = useState<Step>(1)
    const [addressText, setAddressText] = useState('')
    const [manualAddressEntry, setManualAddressEntry] = useState(false)
    const [addressRegion, setAddressRegion] = useState<AddressRegion | null>(() =>
        formData.address1 ? (formData.country === 'GB' ? 'uk' : 'international') : null,
    )
    const [nationalInsuranceTouched, setNationalInsuranceTouched] = useState(false)
    const [signatureTouched, setSignatureTouched] = useState(false)
    const [previewDocuments, setPreviewDocuments] = useState<PreviewDocument[]>([])
    const [previewLoading, setPreviewLoading] = useState(false)
    const [previewError, setPreviewError] = useState<string | null>(null)
    const signCanvas = useRef<SignatureCanvas>(null) as React.MutableRefObject<SignatureCanvas | null>
    const previewRequestId = useRef(0)
    const [firstnameTouched, setFirstnameTouched] = useState(false)
    const [lastnameTouched, setLastnameTouched] = useState(false)
    const [address1Touched, setAddress1Touched] = useState(false)
    const [address2Touched, setAddress2Touched] = useState(false)
    const [cityTouched, setCityTouched] = useState(false)
    const [postCodeTouched, setPostCodeTouched] = useState(false)
    const [countryTouched, setCountryTouched] = useState(false)
    const [buildingNumberMissing, setBuildingNumberMissing] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showServiceBranchErrors, setShowServiceBranchErrors] = useState(false)
    const [titleTouched, setTitleTouched] = useState(false)
    const [showAddressErrors, setShowAddressErrors] = useState(false)
    const formRef = useRef<HTMLFormElement>(null)

    // Address fields animate in once an address is picked (or "enter manually"
    // is pressed), but not when they're already filled as the address step arrives
    // (checked as the step renders, before the fields mount and animate)
    const addressFieldsShownOnArrival = useRef(false)
    const lastRenderedStep = useRef<Step | null>(null)
    if (lastRenderedStep.current !== currentStep) {
        lastRenderedStep.current = currentStep
        addressFieldsShownOnArrival.current =
            currentStep === 3 && Boolean(addressText || formData.address1 || manualAddressEntry)
    }
    const addressFieldsAppear = !addressFieldsShownOnArrival.current

    // Service cards grow in when added and fold away when removed, rather than
    // snapping. Cards are tracked by id; the latest list is kept in a ref so a
    // removal that finishes after another one still finds the right index.
    const serviceRefs = useRef(new Map<string, HTMLDivElement>())
    const servicesRef = useRef(formData.services)
    servicesRef.current = formData.services
    const prevServiceIds = useRef(formData.services.map((s) => s.id))
    const leavingServiceIds = useRef(new Set<string>())

    useLayoutEffect(() => {
        const prev = prevServiceIds.current
        prevServiceIds.current = formData.services.map((s) => s.id)
        if (reducedMotion()) return
        formData.services.forEach((service) => {
            if (prev.includes(service.id)) return
            const el = serviceRefs.current.get(service.id)
            if (!el) return
            // From folded to the card's own (CSS) height and padding
            gsap.set(el, { overflow: 'hidden' })
            gsap.from(el, { ...SERVICE_FOLDED, duration: 0.4, ease: 'power3.out', clearProps: 'all' })
        })
    }, [formData.services])

    const handleRemoveService = (id: string) => {
        if (leavingServiceIds.current.has(id)) return
        const remove = () => {
            leavingServiceIds.current.delete(id)
            const index = servicesRef.current.findIndex((s) => s.id === id)
            if (index !== -1) removeService(index)
        }
        const el = serviceRefs.current.get(id)
        if (!el || reducedMotion()) return remove()
        leavingServiceIds.current.add(id)
        gsap.set(el, { overflow: 'hidden', pointerEvents: 'none' })
        gsap.to(el, { ...SERVICE_FOLDED, duration: 0.35, ease: 'power3.inOut', onComplete: remove })
    }


    const serviceNumberRegex = /^(?:\d{6}|\d{7}|\d{8}|[DWCNPV][0-9]{6}[A-Z]|3[0-9]{7})$/i
    const niNumberRegex = /^[A-CEGHJ-PR-TW-Z]{2}\d{6}[A-D]$/i
    // Any Latin letter incl. accents (Müller, José), plus spaces, apostrophes, hyphens and full stops
    const namePattern = /^\p{Script=Latin}[\p{Script=Latin}\p{M} '’.-]*$/u
    const phonePattern = /^[0-9+() -]+$/
    const isoDateValid = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value)

    const getFirstnameError = (): string | undefined => {
        if (!firstnameTouched) return undefined
        const val = formData.firstname.trim()
        if (val.length === 0) return 'First name is required'
        if (val.length < 2) return 'First name must be at least 2 characters'
        if (val.length > 50) return 'First name must be 50 characters or less'
        if (!namePattern.test(val)) return 'First name can only contain letters, spaces, apostrophes and hyphens'
        return undefined
    }

    const getLastnameError = (): string | undefined => {
        if (!lastnameTouched) return undefined
        const val = formData.lastname.trim()
        if (val.length === 0) return 'Last name is required'
        if (val.length < 2) return 'Last name must be at least 2 characters'
        if (val.length > 50) return 'Last name must be 50 characters or less'
        if (!namePattern.test(val)) return 'Last name can only contain letters, spaces, apostrophes and hyphens'
        return undefined
    }

    const getServiceDischargeError = (service: ServiceRecord): string | undefined => {
        if (service.activeService) return undefined
        if (!service.dischargeDate) return undefined
        if (service.enlistmentDate && service.dischargeDate < service.enlistmentDate) {
            return 'Discharge date must be on or after enlistment date'
        }
        const year = parseInt(service.dischargeDate.slice(0, 4), 10)
        if (year < 1987) return 'Discharge year must be 1987 or later'
        if (year > new Date().getFullYear()) return 'Discharge date cannot be in the future'
        return undefined
    }

    const isServiceValid = (service: ServiceRecord): boolean => {
        const number = service.serviceNumber.trim()
        if (!serviceNumberRegex.test(number)) return false
        if (!service.branch) return false
        if (!isoDateValid(service.enlistmentDate)) return false
        if (service.activeService) return true
        if (!isoDateValid(service.dischargeDate)) return false
        return getServiceDischargeError(service) === undefined
    }

    // After the errors render, bring the first highlighted field into view. Every
    // field's error state (red border or message) uses a class containing "error".
    const scrollToFirstError = () => {
        setTimeout(() => {
            // Skip fields folded away (e.g. discharge date while still serving)
            const firstError = Array.from(
                formRef.current?.querySelectorAll<HTMLElement>('[class*="rror"]') ?? [],
            ).find((el) => !el.closest('[aria-hidden="true"]'))
            firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 50)
    }

    const { transition, stepClassName, isTransitioning } = useStepTransition()

    // Unsigned pack preview: cache by fingerprint of PDF-relevant lead fields.
    // Back → Sign with no edits reuses the last docs; changing details regenerates.
    const previewCache = useRef<{ fingerprint: string; documents: PreviewDocument[] } | null>(null)
    useEffect(() => {
        if (currentStep !== 6) {
            setPreviewLoading(false)
            return
        }

        const fingerprint = getLeadPreviewFingerprint(formData)
        const cached = previewCache.current
        if (cached && cached.fingerprint === fingerprint && cached.documents.length > 0) {
            setPreviewDocuments(cached.documents)
            setPreviewError(null)
            setPreviewLoading(false)
            return
        }

        const requestId = ++previewRequestId.current
        setPreviewLoading(true)
        setPreviewError(null)
        setPreviewDocuments([])

        previewFormDocuments()
            .then((result) => {
                if (requestId !== previewRequestId.current) return
                if (!result.success) {
                    setPreviewError(result.error || 'Could not load your documents for review.')
                    setPreviewDocuments([])
                    return
                }
                const docs = (Array.isArray(result.data?.documents) ? result.data.documents : []).filter(
                    (doc: PreviewDocument) => Boolean(doc?.url)
                )
                setPreviewDocuments(docs)
                if (!docs.length) {
                    setPreviewError('Could not load your documents for review.')
                    return
                }
                previewCache.current = { fingerprint, documents: docs }
            })
            .catch(() => {
                if (requestId !== previewRequestId.current) return
                setPreviewError('Could not load your documents for review.')
                setPreviewDocuments([])
            })
            .finally(() => {
                if (requestId === previewRequestId.current) setPreviewLoading(false)
            })
    }, [currentStep, formData, previewFormDocuments])

    // Slide the current step out and the next one in (backwards if going back).
    // If the stepper has scrolled off screen (e.g. Next pressed at the bottom
    // of a long step), glide back up first and swap steps as the scroll lands:
    // swapping first lets a shorter step make the browser snap the page up.
    const goToStep = (step: Step) => {
        // Don't scroll-only when a transition is already running — previously Back
        // could glide to the top while the step swap was ignored (busy lock).
        if (isTransitioning() || step === currentStep) return
        const direction = step > currentStep ? 'forward' : 'back'
        const swap = () => setCurrentStep(step)
        // Glide to the stepper row, with a little space above it
        const anchor = formRef.current?.querySelector('[data-scroll-anchor]') ?? formRef.current
        const top = anchor?.getBoundingClientRect().top
        const needsScrollUp = top !== undefined && top < SCROLL_UP_GAP

        if (needsScrollUp) {
            gsap.to(window, {
                // No autoKill: on mobile, tapping Next closes the keyboard, which resizes
                // the viewport and nudges the scroll, and autoKill took that for the
                // user scrolling and stopped the glide partway
                scrollTo: { y: Math.max(0, window.scrollY + top - SCROLL_UP_GAP), autoKill: false },
                duration: reducedMotion() ? 0 : SCROLL_UP_MS / 1000,
                ease: 'power2.inOut',
            })
        }

        // Going back: swap immediately (scroll can run in parallel). Holding the
        // leave animation until scroll finished made Sign → NI feel like "only
        // scrolled up" on the long docs step.
        // Going forward from a long step: keep the brief hold so the leave lines up.
        const holdMs =
            needsScrollUp && direction === 'forward' ? SCROLL_UP_MS - STEP_LEAVE_MS : 0
        transition(direction, swap, holdMs)
    }

    const handleNext = () => {
        if (isTransitioning()) return
        if (currentStep === 1) {
            setTitleTouched(true)
            setFirstnameTouched(true)
            setLastnameTouched(true)
            triggerDateErrors()
            if (isBasicDetailsValid()) goToStep(2)
            else scrollToFirstError()
            return
        }
        if (currentStep === 2) {
            triggerEmailErrors()
            triggerPhoneErrors()
            if (isContactValid()) goToStep(3)
            else scrollToFirstError()
            return
        }
        if (currentStep === 3) {
            setShowAddressErrors(true)
            setAddress1Touched(true)
            setCityTouched(true)
            setPostCodeTouched(true)
            if (isAddressValid()) goToStep(4)
            else scrollToFirstError()
            return
        }
        if (currentStep === 4) {
            triggerServiceDateErrors()
            triggerServiceNumberErrors()
            setShowServiceBranchErrors(true)
            if (!isServiceRecordsValid()) {
                scrollToFirstError()
                return
            }
            goToStep(5)
            return
        }
        if (currentStep === 5) {
            setNationalInsuranceTouched(true)
            if (!isIdNumbersValid()) {
                scrollToFirstError()
                return
            }
            goToStep(6)
        }
    }

    const handleBack = () => {
        if (isTransitioning()) return
        if (currentStep > 1) goToStep((currentStep - 1) as Step)
    }

    const handleSubmit = async () => {
        if (!isIdNumbersValid()) {
            setNationalInsuranceTouched(true)
            goToStep(5)
            return
        }
        if (!isSignatureValid()) {
            setSignatureTouched(true)
            scrollToFirstError()
            return
        }

        // The Submit button shows the loading state; the signing page has its own loader
        setIsSubmitting(true)

        const result = await submitFormData()

        if (result.success) {
            trackMetaLead()
            const signingToken = result.data?.signing_token
            if (!signingToken) {
                setIsSubmitting(false)
                toast.error('Submission succeeded but no signing token was returned')
                return
            }
            if (onComplete) onComplete(formData)
            navigate(`/hearing-loss/sign/${encodeURIComponent(signingToken)}`)
        } else {
            setIsSubmitting(false)
            toast.error(
                result.code === 'duplicate_person' || /already exists/i.test(String(result.error || ''))
                    ? result.error ||
                          'A claim with these details already exists. If this is you, use the signing link we sent or contact Sentinel Legal.'
                    : result.error || 'Submission failed — please try again',
            )
        }
    }

    const isBasicDetailsValid = (): boolean => {
        if (formData.title === '') return false
        const fname = formData.firstname.trim()
        if (fname.length < 2 || fname.length > 50 || !namePattern.test(fname)) return false
        const lname = formData.lastname.trim()
        if (lname.length < 2 || lname.length > 50 || !namePattern.test(lname)) return false
        if (formData.dob === '') return false
        const parts = formData.dob.split('-')
        if (parts.length !== 3) return false
        if (parseInt(parts[0], 10) < 1900) return false
        return true
    }

    const selectAddressRegion = (region: AddressRegion) => {
        if (region === addressRegion) return
        setAddressRegion(region)
        // A fresh address search, so its fields should animate in again
        addressFieldsShownOnArrival.current = false
        // Switching between UK and international lookups starts the address from scratch
        onFieldChange('address1', '')
        onFieldChange('address2', '')
        onFieldChange('city', '')
        onFieldChange('postcode', '')
        onFieldChange('country', region === 'uk' ? 'GB' : '')
        setAddressText('')
        setManualAddressEntry(false)
        setAddress1Touched(false)
        setAddress2Touched(false)
        setCityTouched(false)
        setPostCodeTouched(false)
        setCountryTouched(false)
        setBuildingNumberMissing(false)
    }

    const selectGoogleAddress = (address: GooglePlaceAddress, label: string) => {
        onFieldChange('address1', address.address1)
        onFieldChange('address2', address.address2)
        onFieldChange('city', address.city)
        onFieldChange('postcode', address.postcode)
        onFieldChange('country', address.country)
        setAddressText(label)
        setBuildingNumberMissing(!address.hasBuildingNumber)
        // Show any UK postcode problem straight away (Google can return a partial one like "LE6")
        if (address.country === 'GB') setPostCodeTouched(true)
        if (!address.hasBuildingNumber) {
            setAddress1Touched(true)
            // Fields render after this pick, so wait a tick before focusing
            setTimeout(() => document.getElementById('address1')?.focus(), 0)
        }
    }

    const onAddress1Change = (value: string) => {
        onFieldChange('address1', value)
        // Any edit counts as adding the number, so named houses without one aren't blocked
        if (buildingNumberMissing) setBuildingNumberMissing(false)
    }

    const getAddress1Error = (): string | undefined => {
        if (!address1Touched) return undefined
        if (formData.address1.trim() === '') return 'Address line 1 is required'
        if (formData.address1.length > 100) return 'Address line 1 must be 100 characters or less'
        if (buildingNumberMissing) return 'Please add your house or building number'
        return undefined
    }

    const getAddress2Error = (): string | undefined => {
        if (!address2Touched) return undefined
        if (formData.address2.length > 100) return 'Address line 2 must be 100 characters or less'
        return undefined
    }

    const getCityError = (): string | undefined => {
        if (!cityTouched) return undefined
        if (formData.city.trim() === '') return 'Town / city is required'
        if (formData.city.length > 100) return 'Town / city must be 100 characters or less'
        return undefined
    }

    const getCountryError = (): string | undefined => {
        if (!countryTouched) return undefined
        if (formData.country === '') return 'Country is required'
        return undefined
    }

    // UK postcode rules follow the address's country, so a UK address added via "Outside the UK" still needs one
    const usesUkPostcode = addressRegion === 'uk' || formData.country === 'GB'
    const ukPostcodeRegex = /^(GIR ?0AA|[A-Z]{1,2}\d[A-Z\d]? ?\d[ABD-HJLNP-UW-Z]{2})$/i

    const getPostCodeError = (): string | undefined => {
        if (!postCodeTouched) return undefined
        if (!usesUkPostcode) {
            if (formData.postcode.length > 20) return 'Postcode must be 20 characters or less'
            return undefined
        }
        if (formData.postcode.trim() === '') return 'Postcode is required'
        if (!ukPostcodeRegex.test(formData.postcode.trim())) return 'Please enter a valid UK postcode'
        return undefined
    }

    const isIdNumbersValid = (): boolean => {
        if (!niNumberRegex.test(formData.nationalInsurance.trim().toUpperCase())) return false
        return true
    }

    const isSignatureValid = (): boolean =>
        formData.sigData.length > 0 && formData.signatureConsentChecked === true

    const hasSignature = formData.sigData.length > 0
    const allConsentsConfirmed = formData.signatureConsentChecked === true
    const consentProgressLabel = `${allConsentsConfirmed ? 1 : 0} of 1 confirmed`
    const signatureBlockedMessage = !hasSignature
        ? 'Please add your signature above to continue.'
        : !allConsentsConfirmed
          ? 'Please confirm you authorise us to apply your signature.'
          : null

    const saveSign = () => {
        const canvas = signCanvas.current
        if (!canvas) return
        canvas.on()
        setSigData(canvas.toDataURL('image/png'))
    }

    const clearSign = (e: React.MouseEvent) => {
        e.preventDefault()
        const canvas = signCanvas.current
        if (!canvas) return
        canvas.clear()
        canvas.on()
        setSigData('')
    }

    const isServiceRecordsValid = (): boolean =>
        formData.services.length >= 1 && formData.services.length <= 3 && formData.services.every(isServiceValid)

    const isContactValid = (): boolean => {
        if (formData.email.trim() === '') return false
        if (formData.telephone.trim().length < 7 || formData.telephone.length > 25) return false
        if (!phonePattern.test(formData.telephone.trim())) return false
        return true
    }

    const isAddressValid = (): boolean => {
        if (formData.address1.trim() === '' || formData.address1.length > 100) return false
        if (buildingNumberMissing) return false
        if (formData.address2.length > 100) return false
        if (formData.city.trim() === '' || formData.city.length > 100) return false
        if (!addressRegion) return false
        if (addressRegion === 'international' && formData.country === '') return false
        if (!usesUkPostcode) return formData.postcode.length <= 20
        return ukPostcodeRegex.test(formData.postcode.trim())
    }

    // Use their first name once we have it, so later steps feel like a conversation
    const firstName = formData.firstname.trim()
    const displayName = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : ''
    const stepHeading: React.ReactNode =
        displayName && currentStep === 2 ? (
            <>
                <HeadingMuted>Thanks, {displayName}.</HeadingMuted> Now some contact details.
            </>
        ) : (
            STEP_LABELS[currentStep - 1]
        )

    const isEligible =
        formData.servedUkArmedForces === true &&
        formData.servedAfter1987 === true &&
        formData.hearingLossOrTinnitus === true


    return (
        <>
            {/* A real form so browsers (Safari on iPhone especially) offer saved
                name, contact and address details. Never submits: Next/Submit are
                plain buttons, and Enter is ignored. */}
            <form ref={formRef} onSubmit={(e) => e.preventDefault()} noValidate autoComplete="on">
                <FormTemplate
                    heading="SUBMIT YOUR CLAIM"
                    progress={JOURNEY_PROGRESS.claim[currentStep - 1]}
                    // Steps 1–5 = Your details; signature step = Sign
                    currentStep={currentStep === 6 ? 3 : 2}
                    // Like the FAQs below the form, the news logos go once they're claiming
                    hideSocialProof
                >
                    {/* Keyed per step, so each heading scrambles in as its step appears */}
                    <div key={`heading-${currentStep}`}>
                        {/* The "you qualify" moment heads the first step in place of its title */}
                        {currentStep === 1 && isEligible ? (
                            <div className={styles.eligibleIntro} role="status">
                                <svg className={styles.eligibleTick} width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
                                    <circle cx="20" cy="20" r="20" fill="currentColor" />
                                    <path
                                        d="M12 20.5l5.5 5.5L28.5 15"
                                        fill="none"
                                        stroke="var(--eligible-tick)"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <div>
                                    <p className={styles.eligibleTitle}>Good news, you could claim compensation!</p>
                                    <p className={styles.eligibleText}>Let's start your claim with a few basic details.</p>
                                </div>
                            </div>
                        ) : (
                            <Heading scrambleIn>{stepHeading}</Heading>
                        )}
                    </div>
                    <div
                        key={currentStep}
                        className={`${styles.fieldsContainer} ${styles.stepContent} ${stepClassName} ${currentStep === 2 ? styles.contactFields : ''}`}
                    >
                        {currentStep === 1 ? (
                            <>
                                <div className={styles.fieldWrapper}>
                                    <span className={styles.label} id="title-label">
                                        Title
                                    </span>
                                    <div className={styles.titleOptions} role="radiogroup" aria-labelledby="title-label">
                                        {TITLE_OPTIONS.map((t) => (
                                            <button
                                                key={t}
                                                type="button"
                                                role="radio"
                                                aria-checked={formData.title === t}
                                                className={`${styles.addressRegionButton} ${formData.title === t ? styles.selected : ''} mf-listen`}
                                                onClick={() => onFieldChange('title', t)}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                    {titleTouched && !formData.title && (
                                        <span className={styles.fieldError}>Please select your title</span>
                                    )}
                                </div>
                                <TextInput
                                    id="first-name"
                                    label="First name"
                                    value={formData.firstname}
                                    onChange={(value) => onFieldChange('firstname', value)}
                                    onBlur={() => setFirstnameTouched(true)}
                                    placeholder="Enter your first name"
                                    autocomplete="given-name"
                                    required={true}
                                    maxLength={50}
                                    error={getFirstnameError()}
                                    className="mf-masked mf-listen"
                                />
                                <TextInput
                                    id="last-name"
                                    label="Last name"
                                    value={formData.lastname}
                                    onChange={(value) => onFieldChange('lastname', value)}
                                    onBlur={() => setLastnameTouched(true)}
                                    placeholder="Enter your last name"
                                    autocomplete="family-name"
                                    required={true}
                                    maxLength={50}
                                    error={getLastnameError()}
                                    className="mf-masked mf-listen"
                                />
                                <InputFieldDate
                                    fieldName="dob"
                                    label="Date of birth"
                                    birthday
                                    saveValue={saveDateOfBirth}
                                    populateValue={formData.dob}
                                    required={true}
                                    showErrors={showDateErrors}
                                    className="mf-masked mf-listen"
                                />
                            </>
                        ) : currentStep === 2 ? (
                            <>
                                <InputFieldText
                                    fieldName="email"
                                    label="Email address"
                                    autoComplete="email"
                                    saveValue={saveEmail}
                                    populateValue={formData.email}
                                    fieldType="email"
                                    required={true}
                                    showErrors={showEmailErrors}
                                    placeholder="Enter your email"
                                    className="mf-masked mf-listen"
                                />
                                <InputFieldText
                                    fieldName="telephone"
                                    label="Phone number"
                                    // Number only: the dial code picker beside it holds the country code
                                    autoComplete="tel-national"
                                    saveValue={savePhone}
                                    populateValue={formData.telephone}
                                    fieldType="tel"
                                    showDialCode={true}
                                    required={true}
                                    showErrors={showPhoneErrors}
                                    placeholder="Enter your phone number"
                                    minLength={7}
                                    maxLength={20}
                                    className="mf-masked mf-listen"
                                />
                            </>
                        ) : currentStep === 3 ? (
                            <>
                                <div className={styles.addressGroup}>
                                    <div className={styles.fieldWrapper}>
                                        <div className={styles.addressRegion}>
                                            <button
                                                type="button"
                                                className={`${styles.addressRegionButton} ${addressRegion === 'uk' ? styles.selected : ''}`}
                                                onClick={() => selectAddressRegion('uk')}
                                                aria-pressed={addressRegion === 'uk'}
                                            >
                                                In the UK
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles.addressRegionButton} ${addressRegion === 'international' ? styles.selected : ''}`}
                                                onClick={() => selectAddressRegion('international')}
                                                aria-pressed={addressRegion === 'international'}
                                            >
                                                Outside the UK
                                            </button>
                                        </div>
                                        {showAddressErrors && !addressRegion && (
                                            <span className={styles.fieldError}>Please choose where you live</span>
                                        )}
                                    </div>
                                    {addressRegion === 'uk' && (
                                        <InputFieldAddressLookup
                                            fieldName="searchAddress"
                                            label="Enter your postcode"
                                            saveValue={(obj) => {
                                                setAddressText(obj.value)
                                                return saveAddress(obj)
                                            }}
                                            enhanced={true}
                                            className="mf-masked mf-listen"
                                        />
                                    )}
                                    {addressRegion === 'international' && (
                                        <InputFieldGoogleAddressLookup
                                            fieldName="searchAddress"
                                            label="Start typing your street and house number"
                                            onSelect={selectGoogleAddress}
                                            enhanced={true}
                                            className="mf-masked mf-listen"
                                        />
                                    )}
                                    {showAddressErrors &&
                                        addressRegion &&
                                        !manualAddressEntry &&
                                        !addressText &&
                                        !formData.address1 && (
                                            <span className={styles.fieldError}>
                                                Please search for your address or enter it manually
                                            </span>
                                        )}
                                    {addressRegion && !manualAddressEntry && !addressText && !formData.address1 && (
                                        <button
                                            type="button"
                                            className={styles.manualAddressButton}
                                            onClick={() => setManualAddressEntry(true)}
                                        >
                                            Enter address manually
                                        </button>
                                    )}
                                    {addressRegion && (addressText || formData.address1 || manualAddressEntry) && (
                                        <Reveal appear={addressFieldsAppear} className={styles.addressGroup}>
                                            <TextInput
                                                id="address1"
                                                label="Address line 1"
                                                autocomplete="address-line1"
                                                value={formData.address1}
                                                onChange={onAddress1Change}
                                                onBlur={() => setAddress1Touched(true)}
                                                placeholder="Address line 1"
                                                required={true}
                                                maxLength={100}
                                                error={getAddress1Error()}
                                                className="mf-masked mf-listen"
                                            />
                                            <TextInput
                                                label="Address line 2 (optional)"
                                                autocomplete="address-line2"
                                                value={formData.address2}
                                                onChange={(value) => onFieldChange('address2', value)}
                                                onBlur={() => setAddress2Touched(true)}
                                                placeholder="Address line 2"
                                                maxLength={100}
                                                error={getAddress2Error()}
                                                className="mf-masked mf-listen"
                                            />
                                            <TextInput
                                                label="Town / city"
                                                autocomplete="address-level2"
                                                value={formData.city}
                                                onChange={(value) => onFieldChange('city', value)}
                                                onBlur={() => setCityTouched(true)}
                                                placeholder="Town / city"
                                                required={true}
                                                maxLength={100}
                                                error={getCityError()}
                                                className="mf-masked mf-listen"
                                            />
                                            <TextInput
                                                label={usesUkPostcode ? 'Postcode' : 'Postcode / ZIP code (optional)'}
                                                autocomplete="postal-code"
                                                value={formData.postcode}
                                                onChange={(value) => onFieldChange('postcode', value)}
                                                onBlur={() => setPostCodeTouched(true)}
                                                placeholder={usesUkPostcode ? 'Postcode' : 'Postcode / ZIP code'}
                                                required={usesUkPostcode}
                                                error={getPostCodeError()}
                                                className="mf-masked mf-listen"
                                            />
                                            {addressRegion === 'international' && (
                                                <div className={`${styles.fieldWrapper} ${styles.floatingSelect}`}>
                                                    <label
                                                        className={`${styles.label} ${formData.country ? styles.labelRaised : ''}`}
                                                        htmlFor="country"
                                                    >
                                                        Country
                                                    </label>
                                                    <select
                                                        id="country"
                                                        autoComplete="country"
                                                        value={formData.country}
                                                        onChange={(e) => onFieldChange('country', e.target.value)}
                                                        onBlur={() => setCountryTouched(true)}
                                                        className={`${styles.select} ${getCountryError() ? styles.selectError : ''} mf-masked mf-listen`}
                                                        required
                                                    >
                                                        <option value="" hidden></option>
                                                        {countryOptions.map((c) => (
                                                            <option key={c.iso2} value={c.iso2}>
                                                                {c.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {getCountryError() && (
                                                        <span className={styles.fieldError}>{getCountryError()}</span>
                                                    )}
                                                </div>
                                            )}
                                        </Reveal>
                                    )}
                                </div>
                            </>
                        ) : currentStep === 6 ? (
                            <div
                                className={`${sigPadStyles.signaturePadContent} ${styles.signStepWithActionBar}`}
                                data-form-block-type="signature"
                            >
                                <Lead>
                                    Sign below, then review your claim documents underneath. They may take a moment to
                                    appear.
                                </Lead>

                                <div className={sigPadStyles.sigPadContainer}>
                                    <p>Please use your mouse, finger or stylus to draw your signature below.</p>
                                    <div>
                                        <SignatureCanvas
                                            ref={signCanvas}
                                            onEnd={saveSign}
                                            clearOnResize={false}
                                            penColor="grey"
                                            canvasProps={{
                                                width: window.innerWidth < 760 ? 350 : 760,
                                                height: 300,
                                                className: 'sigCanvas mf-masked mf-listen',
                                            }}
                                        />
                                        <span onClick={clearSign} style={{ cursor: 'pointer' }}>
                                            Clear
                                        </span>
                                    </div>
                                </div>
                                <FinePrint className={styles.signingNote}>
                                    By signing, you confirm the information provided is accurate and you authorise
                                    Sentinel Legal to apply your signature to the documents below and act on your behalf
                                    in pursuing your military hearing loss claim.
                                </FinePrint>
                                <FinePrint className={styles.signingNote}>
                                    By pressing submit I consent to Sentinel Legal contacting me regarding this claim
                                    via email, phone, or SMS. I understand I can opt out at any time by contacting
                                    Sentinel Legal or using the unsubscribe link in any message.
                                </FinePrint>
                                <div
                                    className={`${styles.checkboxContainer} ${styles.signatureConsent}`}
                                    data-form-block-type="consent_group"
                                >
                                    <InputCheckbox
                                        id="mhl-lead-accept-terms"
                                        displayName={LEAD_SIGNATURE_CONSENT_DISPLAY}
                                        supportingText={LEAD_SIGNATURE_CONSENT_SUPPORTING}
                                        value={formData.signatureConsentChecked}
                                        onChange={(checked) => {
                                            setSignatureTouched(true)
                                            setSignatureConsentChecked(checked)
                                        }}
                                    />
                                </div>
                                {signatureTouched && !isSignatureValid() && (
                                    <p className={styles.fieldError} role="alert">
                                        {!formData.sigData
                                            ? 'Please add your signature.'
                                            : 'Please confirm you authorise us to apply your signature.'}
                                    </p>
                                )}
                                <FinePrint className={styles.signingNote}>
                                    A signed copy will be emailed to you and stored securely against your case
                                    reference.
                                </FinePrint>

                                <div className={styles.previewDocs} aria-busy={previewLoading || undefined}>
                                    {previewLoading && (
                                        <div className={styles.previewSkeletons} role="status" aria-live="polite">
                                            <p className={styles.previewLoadingLabel}>Loading documents…</p>
                                            {Array.from({ length: 7 }, (_, i) => (
                                                <div className={styles.previewSkeletonCard} key={`preview-skel-${i}`}>
                                                    <div className={styles.previewSkeletonTitle} />
                                                    <div className={styles.previewSkeletonLine} />
                                                    <div
                                                        className={`${styles.previewSkeletonLine} ${styles.previewSkeletonLineShort}`}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {!previewLoading && previewError && (
                                        <p className={styles.fieldError} role="alert">
                                            {previewError} You can still sign — we&apos;ll generate them when you
                                            submit.
                                        </p>
                                    )}
                                    {!previewLoading &&
                                        previewDocuments.map((doc) => (
                                            <div className={styles.previewDocItem} key={doc.id}>
                                                <DocumentPreviewCard
                                                    title={doc.title}
                                                    description={
                                                        doc.description ||
                                                        PREVIEW_DOC_DESCRIPTIONS[doc.id] ||
                                                        'Please review this document carefully before signing.'
                                                    }
                                                    url={doc.url}
                                                    actionLabel={doc.title}
                                                />
                                            </div>
                                        ))}
                                </div>
                            </div>
                        ) : currentStep === 4 ? (
                            <>
                                {formData.services.map((service, index) => (
                                    <div
                                        key={service.id}
                                        ref={(el) => {
                                            if (el) serviceRefs.current.set(service.id, el)
                                            else serviceRefs.current.delete(service.id)
                                        }}
                                        className={styles.serviceBlock}
                                    >
                                        <div className={styles.serviceHeader}>
                                            <h3 className={styles.serviceTitle}>Service {index + 1}</h3>
                                            {formData.services.length > 1 && (
                                                <button
                                                    type="button"
                                                    className={styles.removeServiceButton}
                                                    onClick={() => handleRemoveService(service.id)}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                        <TextInput
                                            label="Service number e.g. 24123456"
                                            value={service.serviceNumber}
                                            onChange={(value) =>
                                                setServiceField(index, 'serviceNumber', value.toUpperCase())
                                            }
                                            error={
                                                showServiceNumberErrors && service.serviceNumber.trim() === ''
                                                    ? 'Service number is required'
                                                    : service.serviceNumber.trim() !== '' &&
                                                        !serviceNumberRegex.test(service.serviceNumber.trim())
                                                      ? 'Please enter a valid UK military service number'
                                                      : undefined
                                            }
                                        />
                                        <div className={styles.fieldWrapper}>
                                            <span className={styles.label} id={`serviceBranch-${service.id}`}>
                                                Service branch
                                            </span>
                                            <div
                                                className={styles.branchPills}
                                                role="radiogroup"
                                                aria-labelledby={`serviceBranch-${service.id}`}
                                            >
                                                {SERVICE_BRANCHES.map((b) => (
                                                    <button
                                                        key={b}
                                                        type="button"
                                                        role="radio"
                                                        aria-checked={service.branch === b}
                                                        className={`${styles.branchPill} ${service.branch === b ? styles.selected : ''} mf-listen`}
                                                        onClick={() => setServiceField(index, 'branch', b)}
                                                    >
                                                        {b}
                                                    </button>
                                                ))}
                                            </div>
                                            {showServiceBranchErrors && !service.branch && (
                                                <span className={styles.fieldError}>Please select a service branch</span>
                                            )}
                                        </div>
                                        <InputFieldDate
                                            fieldName={`enlistmentDate-${service.id}`}
                                            label="Enlistment date"
                                            hint="If you are unsure of exact days, use the 1st of that month."
                                            saveValue={(obj) => {
                                                setServiceField(index, 'enlistmentDate', obj.value)
                                                return { success: true, error: '' }
                                            }}
                                            populateValue={service.enlistmentDate}
                                            required={true}
                                            showErrors={showServiceDateErrors}
                                            minYear={new Date().getFullYear() - 80}
                                            maxYear={new Date().getFullYear()}
                                            className="mf-masked mf-listen"
                                        />
                                        {/* Still serving: no discharge date to ask for, so fold it and the OR away */}
                                        <div
                                            className={`${styles.collapsible} ${service.activeService ? styles.collapsed : ''}`}
                                            aria-hidden={service.activeService}
                                        >
                                            <div className={styles.collapsibleInner}>
                                                <div className={styles.dischargeGroup}>
                                                    <InputFieldDate
                                                        // Remounts when "Still serving" changes: ticking it clears the
                                                        // date, and this field ignores an empty value, so it would
                                                        // otherwise keep showing the old date while folded away
                                                        key={`dischargeDate-${service.id}-${service.activeService}`}
                                                        fieldName={`dischargeDate-${service.id}`}
                                                        label="Discharge date"
                                                        saveValue={(obj) => {
                                                            setServiceField(index, 'dischargeDate', obj.value)
                                                            return { success: true, error: '' }
                                                        }}
                                                        populateValue={service.dischargeDate}
                                                        required={true}
                                                        showErrors={showServiceDateErrors && !service.activeService}
                                                        minYear={
                                                            service.enlistmentDate
                                                                ? parseInt(service.enlistmentDate.slice(0, 4), 10)
                                                                : new Date().getFullYear() - 80
                                                        }
                                                        maxYear={new Date().getFullYear()}
                                                        className="mf-masked mf-listen"
                                                    />
                                                    {getServiceDischargeError(service) && (
                                                        <p className={styles.fieldError}>
                                                            {getServiceDischargeError(service)}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className={styles.orDivider} aria-hidden="true">
                                                    <span>OR</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.checkboxContainer}>
                                            <input
                                                type="checkbox"
                                                id={`stillServing-${service.id}`}
                                                checked={service.activeService}
                                                onChange={(e) =>
                                                    setServiceField(index, 'activeService', e.target.checked)
                                                }
                                                className={styles.checkbox}
                                            />
                                            <label
                                                htmlFor={`stillServing-${service.id}`}
                                                className={styles.checkboxLabel}
                                            >
                                                Still serving in this role
                                            </label>
                                        </div>
                                    </div>
                                ))}
                                {formData.services.length < 3 && (
                                    <button type="button" className={styles.addServiceButton} onClick={addService}>
                                        + Add service
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                <TextInput
                                    label="National Insurance number"
                                    hint="Used to request your MOD and HMRC records"
                                    value={formData.nationalInsurance}
                                    onChange={(value) =>
                                        onFieldChange('nationalInsurance', value.toUpperCase().replace(/\s/g, ''))
                                    }
                                    onBlur={() => setNationalInsuranceTouched(true)}
                                    placeholder="e.g. QQ123456C"
                                    maxLength={9}
                                    className="mf-masked mf-listen"
                                    error={
                                        !nationalInsuranceTouched
                                            ? undefined
                                            : formData.nationalInsurance.trim() === ''
                                              ? 'National Insurance number is required'
                                              : !niNumberRegex.test(formData.nationalInsurance.trim().toUpperCase())
                                                ? 'Please enter a valid National Insurance number'
                                                : undefined
                                    }
                                />
                                <div className={styles.checkboxContainer}>
                                    <input
                                        type="checkbox"
                                        id="consentToContactFuture"
                                        checked={formData.consentToContactFuture}
                                        onChange={(e) =>
                                            onFieldChange('consentToContactFuture', e.target.checked.toString())
                                        }
                                        className={styles.checkbox}
                                    />
                                    <label htmlFor="consentToContactFuture" className={styles.checkboxLabel}>
                                        I agree to receive communications about additional services that may be relevant
                                        to me.
                                    </label>
                                </div>
                            </>
                        )}
                    </div>

                    <div className={styles.navigation}>
                        {currentStep === 1 && onBack && <BackButton onClick={onBack} />}
                        {currentStep > 1 && <BackButton onClick={handleBack} />}
                        {currentStep < 6 && <div className={styles.dummyButton} />}
                        {currentStep < 6 && (
                            <SubmitButton
                                onClick={handleNext}
                                looksDisabled={
                                    (currentStep === 1 && !isBasicDetailsValid()) ||
                                    (currentStep === 2 && !isContactValid()) ||
                                    (currentStep === 3 && !isAddressValid()) ||
                                    (currentStep === 4 && !isServiceRecordsValid()) ||
                                    (currentStep === 5 && !isIdNumbersValid())
                                }
                                isSubmitting={false}
                            >
                                Next
                            </SubmitButton>
                        )}
                    </div>
                </FormTemplate>
            </form>

            {currentStep === 6 && (
                <SignatureActionBar
                    onSubmit={handleSubmit}
                    isSubmitBlocked={!isSignatureValid()}
                    blockedMessage={signatureBlockedMessage}
                    isSubmitLoading={isSubmitting}
                    consentProgressLabel={consentProgressLabel}
                    hasSignature={hasSignature}
                    allConsentsConfirmed={allConsentsConfirmed}
                />
            )}
        </>
    )
}

export default ClaimForm
